import ExtensionList from "@/components/extension/ExtensionList";
import Domain from "@/components/breadcrumbs/Domain";
import {Page} from "@/PageDescriptors";
import PickupGroupList from "@/components/pickup-group/PickupGroupList";
import PickupGroup from "@/components/breadcrumbs/PickupGroup";
import IvrList from "@/components/ivr/list/IvrList";
import IvrDeletePage from "@/components/ivr/list/delete/IvrDeletePage";
import LocationList from "@/components/locations/list/LocationList";
import Location from "@/components/breadcrumbs/Location";
import AdminList from "@/components/AdminList";
import DomainListPage from "@/components/domain/DomainListPage";
import {domainPermissionHideHook, EndpointBreadcrumb} from "./tools";
import gatewaysPage from "./gateways";
import {DomainStatusPage} from "@/components/domain-status/DomainStatus";
import ExtensionDashboard from "@/components/extension/dashboard/ExtensionDashboard";
import DomainSummary from "@/components/domain-dashboard/DomainSummary";
import UserManagement from "@/components/extension/user_management/UserManagement";
import UpdatePickupGroup from "@/components/pickup-group/UpdatePickupGroup";
import UpdateLocationPage from "@/components/locations/update/Page";
import RelatedExtensions from "@/components/locations/related-extensions/RelatedExtensions";
import SpeedDialsList from "@/components/extension/speed-dials/SpeedDialsList";
import IvrSuperuserSettings from "@/components/ivr/superuser/IvrSuperuserSettings";
import UpdateIvrProfilesPage from "@/components/ivr/profiles/UpdateIvrProfilesPage";
import PickupGroupMembers from "@/components/pickup-group/members/PickupGroupMembers";
import {Devices} from "@/components/extension/devices/Devices";
import ExtensionLimitSettings from "@/components/extension/limit-settings/ExtensionLimitSettings";
import UpdateExtensionFormPage from "@/components/extension/settings/UpdateExtensionFormPage";
import {extensionFieldsetCall, extensionFieldsetGeneric} from "@/components/extension/settings/fieldSets";
import DeleteExtensionPage from "@/components/extension/delete/DeleteExtensionPage";
import UpdateExtensionProfilesPage from "@/components/extension/profiles/UpdateExtensionProfilesPage";
import {DomainAddressBookPage} from "@/components/address-book/DomainAddressBookPage";
import {CallListPage} from "@/components/call-list/CallListPage";
import {GroupFilterType} from "@/api";
import AddressBookBulk from "@/components/address-book/bulk/AddressBookBulk";
import {CallGroupList, CallQueueList} from "@/components/call-group/CallGroupList";
import {CallGroupMembersPage} from "@/components/call-group/members/CallGroupMembers";
import QueueStatistics from "@/components/call-group/statistics/QueueStatistics";
import AgentStatistics from "@/components/call-group/statistics/AgentStatistics";
import QueueCallListPage from "@/components/call-group/statistics/QueueCallListPage";
import CallQueueSettings from "@/components/call-group/call-queue/CallQueueSettings";
import CallGroupSettings from "@/components/call-group/settings/CallGroupSettings";
import createAllocationPages from "@/components/domain_settings/allocations/createAllocationPages";
import {DomainQuotaDefaultsSettings} from "@/components/domain_settings/quota_defaults/DomainQuotaDefaultsSettings";
import {DomainBulkCapability} from "@/components/domain_settings/bulk_capability/DomainBulkCapability";
import DomainBulkExtension from "@/components/domain_settings/bulk_extension/DomainBulkExtension";
import faxesPage from "@/pages/domains/faxes";
import DomainSettingsPage from "@/components/domain_settings/settings/DomainSettingsPage";
import DomainBulkNonPaymentBlocking from "@/components/domain_settings/bulk_non_payment_blocking/DomainBulkNonPaymentBlocking";
import DeleteCallGroupPage from "@/components/call-group/delete/DeleteCallGroupPage";
import IvrCalls from "@/components/ivr/calls/IvrCalls";
import AllocatedE164Ranges from "@/components/domain_settings/allocated-e164-ranges/AllocatedE164Ranges";
import DomainExport from "@/components/domain/export/DomainExport";
import DomainSettingsRename from "@/components/domain_settings/rename/DomainSettingsRename";
import DomainDefaults from "@/components/domain_settings/defaults/DomainDefaults";
import AuditLogsPage from "../audit-logs/AuditLogs";
import {useFeatureHide} from "@/hooks";
import DomainMediaList from "@/components/domain-media/DomainMediaList";
import {BasePath} from "@/basePaths";
import {personalAddressBookPage} from "../personal-extensions";
import {defineMessage} from "react-intl";
import {commonTranslations} from "@/utils/translations";
import DeleteLocationPage from "@/components/locations/delete/DeleteLocationPage";

const usersPage: Page = {
    path: "users",
    label: defineMessage({id: "users", defaultMessage: "Users"}),
    contentComponent: ExtensionList,
    items: [
        {
            path: ":extensionId",
            label: null,
            breadcrumbComponent: EndpointBreadcrumb("users", "extensionId"),
            contentComponent: (props: {[s: string]: any}) => <ExtensionDashboard extensionId={props.extensionId} />,
            items: [
                {
                    path: "generic",
                    label: defineMessage({id: "generic", defaultMessage: "Generic"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UpdateExtensionFormPage id={props.extensionId} fields={extensionFieldsetGeneric} />
                    ),
                },
                {
                    path: "call",
                    label: defineMessage({id: "call", defaultMessage: "Call"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UpdateExtensionFormPage id={props.extensionId} fields={extensionFieldsetCall} />
                    ),
                },
                {
                    path: "devices",
                    label: defineMessage({id: "devices.title", defaultMessage: "Devices"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <Devices extensionId={props.extensionId} domainId={props.domainId} />
                    ),
                },
                {
                    path: "quota",
                    label: defineMessage({id: "quota", defaultMessage: "Quota"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <ExtensionLimitSettings extensionId={props.extensionId} domainId={props.domainId} />
                    ),
                },
                {
                    path: "users",
                    label: defineMessage({id: "userManagement.label", defaultMessage: "Login management"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UserManagement domainId={props.domainId} extensionId={props.extensionId} />
                    ),
                },
                {
                    path: "profiles",
                    label: defineMessage({id: "profiles", defaultMessage: "Profiles"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UpdateExtensionProfilesPage extensionId={props.extensionId} />
                    ),
                },
                {
                    path: "speed-dials",
                    label: defineMessage({id: "speedDials.title", defaultMessage: "Speed dials"}),
                    contentComponent: (props: {[s: string]: any}) => <SpeedDialsList extensionId={props.extensionId} />,
                },
                personalAddressBookPage,
                {
                    path: "delete",
                    label: defineMessage({id: "delete", defaultMessage: "Delete"}),
                    hideHook: domainPermissionHideHook("delete_extension"),
                    contentComponent: (props: {[s: string]: any}) => (
                        <DeleteExtensionPage extensionId={props.extensionId} domainId={props.domainId} />
                    ),
                },
            ],
        },
    ],
};

const callGroupsPage: Page = {
    path: "call-groups",
    label: defineMessage({id: "callGroups", defaultMessage: "Call groups"}),
    contentComponent: CallGroupList,
    items: [
        {
            path: ":groupId",
            label: null,
            breadcrumbComponent: EndpointBreadcrumb("groups", "groupId"),
            items: [
                {
                    path: "settings",
                    label: defineMessage({id: "settings", defaultMessage: "Settings"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <CallGroupSettings id={props.groupId} domainId={props.domainId} />
                    ),
                },
                {
                    path: "members",
                    label: defineMessage({id: "members", defaultMessage: "Members"}),
                    contentComponent: (props: {[s: string]: any}) => <CallGroupMembersPage groupId={props.groupId} />,
                },
                {
                    path: "delete",
                    label: defineMessage({id: "delete", defaultMessage: "Delete"}),
                    hideHook: domainPermissionHideHook("delete_call_group"),
                    contentComponent: (props: {[s: string]: any}) => (
                        <DeleteCallGroupPage
                            groupId={props.groupId}
                            domainId={props.domainId}
                            groupType={GroupFilterType.CallGroup}
                        />
                    ),
                },
            ],
        },
    ],
};

const pickupGroupsPage: Page = {
    path: "pickup-groups",
    label: defineMessage({id: "pickupGroups", defaultMessage: "Pickup groups"}),
    contentComponent: PickupGroupList,
    items: [
        {
            path: ":groupId",
            label: null,
            breadcrumbComponent: PickupGroup,
            items: [
                {
                    path: "settings",
                    label: defineMessage({id: "settings", defaultMessage: "Settings"}),
                    contentComponent: (props: {[s: string]: any}) => <UpdatePickupGroup id={props.groupId} />,
                },
                {
                    path: "members",
                    label: defineMessage({id: "members", defaultMessage: "Members"}),
                    contentComponent: (props: {[s: string]: any}) => <PickupGroupMembers groupId={props.groupId} />,
                },
            ],
        },
    ],
};

const callQueuesPage: Page = {
    path: "call-queues",
    label: defineMessage({id: "call_queue.section-title", defaultMessage: "Call queues"}),
    contentComponent: CallQueueList,
    items: [
        {
            path: ":groupId",
            label: null,
            breadcrumbComponent: EndpointBreadcrumb("groups", "groupId"),
            items: [
                {
                    path: "settings",
                    label: defineMessage({id: "settings", defaultMessage: "Settings"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <CallQueueSettings domainId={props.domainId} groupId={props.groupId} />
                    ),
                },
                {
                    path: "members",
                    label: defineMessage({id: "members", defaultMessage: "Members"}),
                    contentComponent: (props: {[s: string]: any}) => <CallGroupMembersPage groupId={props.groupId} />,
                },
                {
                    path: "statistics",
                    label: defineMessage({
                        id: "call_queue.statistics.section-label",
                        defaultMessage: "Queue Statistics",
                    }),
                    contentComponent: (props: {[s: string]: any}) => <QueueStatistics groupId={props.groupId} />,
                    hideHook: useFeatureHide("callCenterStatistics"),
                },
                {
                    path: "agent-statistics",
                    label: defineMessage({
                        id: "call_queue.statistics.agent-section-label",
                        defaultMessage: "Agent Statistics",
                    }),
                    contentComponent: (props: {[s: string]: any}) => <AgentStatistics groupId={props.groupId} />,
                    hideHook: useFeatureHide("callCenterStatistics"),
                },
                {
                    path: "calls",
                    label: defineMessage({id: "calls", defaultMessage: "Calls"}),
                    contentComponent: (props: {[s: string]: any}) => <QueueCallListPage groupId={props.groupId} />,
                },
                {
                    path: "delete",
                    label: defineMessage({id: "delete", defaultMessage: "Delete"}),
                    hideHook: domainPermissionHideHook("delete_call_group"),
                    contentComponent: (props: {[s: string]: any}) => (
                        <DeleteCallGroupPage
                            groupId={props.groupId}
                            domainId={props.domainId}
                            groupType={GroupFilterType.CallQueue}
                        />
                    ),
                },
            ],
        },
    ],
};

const autoAttendantsPage: Page = {
    path: "ivrs",
    label: defineMessage({id: "ivr.category-title", defaultMessage: "Auto attendants"}),
    contentComponent: IvrList,
    items: [
        {
            path: ":ivrId",
            label: null,
            breadcrumbComponent: EndpointBreadcrumb("ivrs", "ivrId"),
            items: [
                {
                    path: "settings",
                    label: defineMessage({id: "settings", defaultMessage: "Settings"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <IvrSuperuserSettings id={props.ivrId} domainId={props.domainId} />
                    ),
                    permission: "edit_ivr",
                },
                {
                    path: "profiles",
                    label: defineMessage({id: "profiles", defaultMessage: "Profiles"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UpdateIvrProfilesPage ivrId={props.ivrId} domainId={props.domainId} />
                    ),
                    permission: "edit_ivr",
                },
                {
                    path: "calls",
                    label: defineMessage({id: "calls", defaultMessage: "Calls"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <IvrCalls ivrId={props.ivrId} domainId={props.domainId} />
                    ),
                    // the user needs view_domain to query any field of an IVR anyways, so it
                    // seems ok to require this permission
                    permission: "view_domain",
                },
                {
                    path: "delete",
                    label: commonTranslations.delete,
                    contentComponent: (props: {[s: string]: any}) => (
                        <IvrDeletePage ivrId={props.ivrId} domainId={props.domainId} />
                    ),
                    permission: "delete_ivr",
                },
            ],
        },
    ],
};

const locationsPage: Page = {
    path: "locations",
    label: defineMessage({id: "locations", defaultMessage: "Locations"}),
    contentComponent: LocationList,
    permission: "view_location",
    items: [
        {
            path: ":locationId",
            label: null,
            breadcrumbComponent: Location,
            items: [
                {
                    path: "settings",
                    label: defineMessage({id: "details", defaultMessage: "Details"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UpdateLocationPage locationId={props.locationId} domainId={props.domainId} />
                    ),
                },
                {
                    path: "extensions",
                    label: defineMessage({id: "relatedExtensions", defaultMessage: "Related users"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <RelatedExtensions locationId={props.locationId} domainId={props.domainId} />
                    ),
                },
                {
                    path: "delete",
                    label: commonTranslations.delete,
                    permission: "delete_location",
                    contentComponent: (props: {[s: string]: any}) => (
                        <DeleteLocationPage locationId={props.locationId} />
                    ),
                },
            ],
        },
    ],
};

const domainsPage: Page<BasePath> = {
    path: "domains",
    label: defineMessage({id: "domains.label", defaultMessage: "Domains"}),
    permission: "view_domain",
    contentComponent: () => <DomainListPage />,
    items: [
        {
            path: ":domainId",
            label: null,
            breadcrumbComponent: Domain,
            contentComponent: (props: {[s: string]: any}) => <DomainSummary domainId={props.domainId} />,
            items: [
                usersPage,
                callGroupsPage,
                pickupGroupsPage,
                callQueuesPage,
                autoAttendantsPage,
                faxesPage,
                gatewaysPage,
                locationsPage,
                {
                    path: "call-records",
                    label: defineMessage({id: "callRecords", defaultMessage: "Call records"}),
                    contentComponent: (p: {[s: string]: any}) => <CallListPage domainId={p.domainId} />,
                },
                {
                    path: "address-book",
                    label: defineMessage({id: "addressBook", defaultMessage: "Address book"}),
                    items: [
                        {
                            path: "list",
                            label: defineMessage({id: "list", defaultMessage: "List"}),
                            contentComponent: (props: {[s: string]: any}) => (
                                <DomainAddressBookPage domainId={props.domainId} />
                            ),
                        },
                        {
                            path: "bulk",
                            label: defineMessage({id: "bulk", defaultMessage: "Bulk"}),
                            contentComponent: (props: {[s: string]: any}) => (
                                <AddressBookBulk domainId={props.domainId} />
                            ),
                        },
                    ],
                },
                {
                    path: "admins",
                    label: defineMessage({id: "admins", defaultMessage: "Admins"}),
                    contentComponent: AdminList,
                    permission: "view_superuser_admin",
                },
                {
                    path: "media",
                    label: defineMessage({id: "media", defaultMessage: "Media"}),
                    contentComponent: (props: {[s: string]: any}) => <DomainMediaList domainId={props.domainId} />,
                },
                {
                    path: "settings",
                    label: defineMessage({id: "settings", defaultMessage: "Settings"}),
                    items: [
                        {
                            path: "generic",
                            label: defineMessage({id: "genericSettings", defaultMessage: "Generic settings"}),
                            permission: "edit_domain_settings",
                            contentComponent: (p: {[s: string]: any}) => <DomainSettingsPage id={p.domainId} />,
                        },
                        {
                            path: "rename",
                            label: defineMessage({id: "rename", defaultMessage: "Rename"}),
                            // if someone can add a domain, it can be implied that they can rename it as well
                            permission: "add_domain",
                            contentComponent: (p: {[s: string]: any}) => <DomainSettingsRename domainId={p.domainId} />,
                        },
                        {
                            path: "defaults",
                            label: defineMessage({id: "defaults", defaultMessage: "Defaults"}),
                            contentComponent: (props: {[s: string]: any}) => (
                                <DomainDefaults domainId={props.domainId} />
                            ),
                        },
                        {
                            path: "quota",
                            label: defineMessage({id: "quota", defaultMessage: "Quota"}),
                            permission: "view_domain",
                            items: [
                                ...createAllocationPages(),
                                {
                                    path: "defaults",
                                    label: defineMessage({id: "defaults", defaultMessage: "Defaults"}),
                                    contentComponent: (props: {[s: string]: any}) => (
                                        <DomainQuotaDefaultsSettings domainId={props.domainId} />
                                    ),
                                },
                                {
                                    path: "bulk-capability-modification",
                                    label: defineMessage({
                                        id: "bulk_capability_modification",
                                        defaultMessage: "Bulk capability modification",
                                    }),
                                    contentComponent: (props: {[s: string]: any}) => (
                                        <DomainBulkCapability domainId={props.domainId} />
                                    ),
                                },
                            ],
                        },
                        {
                            path: "allocated-e164-ranges",
                            label: defineMessage({
                                id: "allocatedE164Ranges.label",
                                defaultMessage: "Allocated E164 ranges",
                            }),
                            contentComponent: (props: {[s: string]: any}) => (
                                <AllocatedE164Ranges domainId={props.domainId} />
                            ),
                            permission: "add_extension",
                        },
                        {
                            path: "bulk-user-modification",
                            label: defineMessage({
                                id: "bulk_user_modification.title",
                                defaultMessage: "Bulk user modification",
                            }),
                            contentComponent: (props: {[s: string]: any}) => (
                                <DomainBulkExtension domainId={props.domainId} />
                            ),
                            hideHook: domainPermissionHideHook("edit_extension"),
                        },
                        {
                            path: "bulk-non-payment-blocking",
                            label: defineMessage({
                                id: "bulkNonPaymentBlocking.title",
                                defaultMessage: "Bulk non-payment blocking",
                            }),
                            contentComponent: (props: {[s: string]: any}) => (
                                <DomainBulkNonPaymentBlocking domainId={props.domainId} />
                            ),
                            permission: "edit_superuser_domain",
                        },
                        {
                            path: "export",
                            label: defineMessage({id: "export", defaultMessage: "Export"}),
                            permission: "edit_superuser_domain",
                            contentComponent: (props: {[s: string]: any}) => <DomainExport domainId={props.domainId} />,
                        },
                    ],
                },
                {
                    path: "status",
                    label: defineMessage({id: "status", defaultMessage: "Status"}),
                    contentComponent: DomainStatusPage,
                },
                {
                    path: "audit-logs",
                    label: defineMessage({id: "audit_logs", defaultMessage: "Audit logs"}),
                    contentComponent: (props: {[s: string]: any}) => <AuditLogsPage domainId={props.domainId} />,
                    hideHook: useFeatureHide("auditLog"),
                },
            ],
        },
    ],
};

export default domainsPage;
